import {AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, HostListener, OnDestroy, OnInit} from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {HubProcessEditorResponse, HubProcessEditorScreen} from '../../model/hub-session';
import {HubApiService} from '../../services/hub-api.service';
import {HelpTooltipComponent} from '../../components/help-tooltip/help-tooltip.component';
import {TranslationsComponent} from '../translations/translations.component';
import {WorkflowEditorComponent} from './workflow-editor/workflow-editor.component';
import {UnsavedChangesComponent} from '../../guards/unsaved-changes.guard';
import '@web.awesome.me/webawesome-pro/dist/components/icon/icon.js';
type DataModelScopeMode = 'readonly' | 'writeonly' | 'readwrite' | 'local';
type DataModelMandatory = 'mandatory' | 'optional';
type DataModelTypeName = 'Text' | 'Number' | 'Boolean' | 'Email' | 'Phone' | 'Country' | 'ChUid' | 'File' | 'ComponentList';
type ScreenElementType = 'control' | 'title' | 'label' | 'infobox' | 'output' | 'twocolumns' | 'componentlist';
type ScreenTextSource = 'text' | 'variable' | 'binding';
type ProcessEditorTab = 'properties' | 'datamodel' | 'screens' | 'workflow' | 'translations';

interface DataModelTextOption {
  value: string;
  enabledCondition: string;
}

interface DataModelTextType {
  typeName: 'Text';
  maxLength: number | null;
  hasOptions: boolean;
  multiline: boolean;
  optionsList: DataModelTextOption[];
}

interface DataModelNumberType {
  typeName: 'Number';
  defaultValue: number | null;
  minValue: number | null;
  maxValue: number | null;
  step: number | null;
}

interface DataModelLengthType {
  typeName: 'Email' | 'Phone';
  maxLength: number | null;
}

interface DataModelFileType {
  typeName: 'File';
  maxSize: number | null;
  allowMultiple: boolean;
}

interface DataModelComponentListType {
  typeName: 'ComponentList';
  key_variable_name: string;
  id: string;
  children: DataModelVariable[];
}

interface DataModelSimpleType {
  typeName: 'Boolean' | 'Country' | 'ChUid';
}

type DataModelType = DataModelTextType | DataModelNumberType | DataModelLengthType | DataModelFileType | DataModelComponentListType | DataModelSimpleType;

interface DataModelVariable {
  name: string;
  scopeMode: DataModelScopeMode;
  mandatory: DataModelMandatory;
  visibilityCondition: string;
  enabledCondition: string;
  type: DataModelType;
  editorOpen: boolean;
  childrenOpen: boolean;
}

interface ScreenElement {
  type: ScreenElementType;
  binding: string;
  text: string;
  label: string;
  visibilityCondition: string;
  textSource: ScreenTextSource;
  script: string;
  valueTypeEnabled: boolean;
  valueTypeType: string;
  valueTypeStep: number | null;
  editorOpen: boolean;
  childrenOpen: boolean;
  children: ScreenElement[];
  raw: Record<string, unknown>;
}

interface ScreenEditorState {
  visualReady: boolean;
  parseError: JsonEditorError | null;
  mode: 'gui' | 'json';
  rawDirty: boolean;
  elements: ScreenElement[];
}

interface JsonEditorError {
  title: string;
  details: string[];
}

interface ProcessEditorDraft {
  properties?: {
    type: string;
    locus: string;
    description: string;
  };
  datamodel?: {
    json: string;
    variables: DataModelVariable[];
    parseError: JsonEditorError | null;
    visualReady: boolean;
    mode: 'gui' | 'json';
    rawDirty: boolean;
  };
  screens: Record<string, {
    json: string;
    state: ScreenEditorState;
  }>;
  workflow?: {
    json: string;
  };
}

interface AutosaveRequest {
  action: string;
  payload: Record<string, unknown>;
}

@Component({
  selector: 'hub-process-editor',
  imports: [NgTemplateOutlet, FormsModule, DatePipe, RouterLink, HelpTooltipComponent, TranslationsComponent, WorkflowEditorComponent],
  templateUrl: './process-editor.component.html',
  styleUrl: './process-editor.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ProcessEditorComponent implements OnInit, AfterViewInit, OnDestroy, UnsavedChangesComponent {
  readonly dataModelTypes: Array<{ value: DataModelTypeName; labelKey: string }> = [
    {value: 'Text', labelKey: 'HubTypeText'},
    {value: 'Number', labelKey: 'HubTypeNumber'},
    {value: 'Boolean', labelKey: 'HubTypeBoolean'},
    {value: 'Email', labelKey: 'HubTypeEmail'},
    {value: 'Phone', labelKey: 'HubTypePhone'},
    {value: 'Country', labelKey: 'HubTypeCountry'},
    {value: 'ChUid', labelKey: 'HubTypeChUid'},
    {value: 'File', labelKey: 'HubTypeFile'},
    {value: 'ComponentList', labelKey: 'HubTypeComponentList'}
  ];
  readonly scopeModeOptions: Array<{ value: DataModelScopeMode; labelKey: string }> = [
    {value: 'readwrite', labelKey: 'HubScopeReadWrite'},
    {value: 'readonly', labelKey: 'HubScopeReadOnly'},
    {value: 'writeonly', labelKey: 'HubScopeWriteOnly'},
    {value: 'local', labelKey: 'HubScopeLocal'}
  ];
  readonly mandatoryOptions: Array<{ value: DataModelMandatory; labelKey: string }> = [
    {value: 'optional', labelKey: 'HubMandatoryOptional'},
    {value: 'mandatory', labelKey: 'HubMandatoryRequired'}
  ];
  readonly screenElementTypes: Array<{ value: ScreenElementType; labelKey: string; icon: string }> = [
    {value: 'control', labelKey: 'HubScreenElementControl', icon: 'input-cursor-text'},
    {value: 'title', labelKey: 'HubScreenElementTitle', icon: 'type-h1'},
    {value: 'label', labelKey: 'HubScreenElementLabel', icon: 'card-text'},
    {value: 'infobox', labelKey: 'HubScreenElementInfobox', icon: 'info-circle'},
    {value: 'output', labelKey: 'HubScreenElementOutput', icon: 'calculator'},
    {value: 'twocolumns', labelKey: 'HubScreenElementTwoColumns', icon: 'layout-split'},
    {value: 'componentlist', labelKey: 'HubScreenElementComponentList', icon: 'collection'}
  ];
  readonly screenTextSourceOptions: Array<{ value: ScreenTextSource; labelKey: string }> = [
    {value: 'text', labelKey: 'HubScreenTextSourceText'},
    {value: 'variable', labelKey: 'HubScreenTextSourceVariable'},
    {value: 'binding', labelKey: 'HubScreenTextSourceBinding'}
  ];
  private readonly autosaveDelayMs = 500;
  private readonly fieldAutosaveDelayMs = 1200;

  data: HubProcessEditorResponse | null = null;
  datamodelVariables: DataModelVariable[] = [];
  screenEditorStates: Record<string, ScreenEditorState> = {};
  datamodelParseError: JsonEditorError | null = null;
  datamodelVisualReady = false;
  datamodelMode: 'gui' | 'json' = 'gui';
  rawDatamodelDirty = false;
  processDefinitionId = 0;
  activeTab: ProcessEditorTab = 'properties';
  selectedScreenId = '';
  newScreenId = '';
  datamodelHelpOpen = false;
  loading = true;
  error = '';
  success = '';
  private propertiesDirty = false;
  private datamodelDirty = false;
  private screenDirtyIds = new Set<string>();
  private workflowDirty = false;
  private viewInitialized = false;
  private autosaveTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  private autosaveRequests: Record<string, AutosaveRequest> = {};
  private autosaveInFlight = new Set<string>();
  private queuedAutosaves = new Set<string>();

  constructor(private hubApi: HubApiService, private route: ActivatedRoute) {
  }

  readonly tabItems: Array<{ value: ProcessEditorTab; labelKey: string }> = [
    {value: 'properties', labelKey: 'processProperties'},
    {value: 'datamodel', labelKey: 'HubDataModel'},
    {value: 'screens', labelKey: 'HubScreens'},
    {value: 'workflow', labelKey: 'HubWorkflow'},
    {value: 'translations', labelKey: 'GlobalLabelsPageLink'}
  ];

  ngOnInit(): void {
    this.processDefinitionId = Number(this.route.snapshot.paramMap.get('processDefinitionId'));
    this.reload();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
  }

  ngOnDestroy(): void {
    Object.values(this.autosaveTimers).forEach((timer) => clearTimeout(timer));
    this.autosaveTimers = {};
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges()) return;
    event.preventDefault();
    event.returnValue = this.unsavedChangesMessage();
  }

  markPropertiesDirty(): void {
    this.propertiesDirty = true;
    this.scheduleAutosaveProperties(this.fieldAutosaveDelayMs);
  }

  saveUndoRedoLabel(): string {
    if (this.autosaveInFlight.size > 0) return this.t('HubAutosaving');
    if (this.hasAutosaveWork() || this.hasDirtyChanges()) return this.t('HubAutosaveUnsaved');
    return this.t('HubAutosaved');
  }

  canUndoDatamodel(): boolean {
    return !!this.data?.datamodel.archive.canUndo;
  }

  canRedoDatamodel(): boolean {
    return !!this.data?.datamodel.archive.canRedo;
  }

  undoDatamodel(): void {
    this.restoreDatamodelArchive('undo');
  }

  redoDatamodel(): void {
    this.restoreDatamodelArchive('redo');
  }

  canUndoScreen(screen: HubProcessEditorScreen): boolean {
    return screen.archive.canUndo;
  }

  canRedoScreen(screen: HubProcessEditorScreen): boolean {
    return screen.archive.canRedo;
  }

  undoScreen(screen: HubProcessEditorScreen): void {
    this.restoreScreenArchive(screen, 'undo');
  }

  redoScreen(screen: HubProcessEditorScreen): void {
    this.restoreScreenArchive(screen, 'redo');
  }

  canUndoWorkflow(): boolean {
    return !!this.data?.workflow.archive.canUndo;
  }

  canRedoWorkflow(): boolean {
    return !!this.data?.workflow.archive.canRedo;
  }

  undoWorkflow(): void {
    this.restoreWorkflowArchive('undo');
  }

  redoWorkflow(): void {
    this.restoreWorkflowArchive('redo');
  }

  onDatamodelJsonChange(): void {
    this.rawDatamodelDirty = true;
    this.datamodelDirty = true;
    this.scheduleAutosaveDatamodel(false, this.fieldAutosaveDelayMs);
  }

  applyDatamodelJson(): void {
    if (!this.data) return;
    const wasRawDirty = this.rawDatamodelDirty;
    this.parseDatamodelJson(this.data.datamodel.json);
    if (!this.datamodelParseError) {
      this.rawDatamodelDirty = false;
      this.syncDatamodelJson(wasRawDirty);
      this.scheduleAutosaveDatamodel(wasRawDirty);
    }
  }

  toggleDatamodelMode(): void {
    if (this.datamodelMode === 'gui') {
      this.syncDatamodelJson(false);
      this.datamodelMode = 'json';
      return;
    }

    this.applyDatamodelJson();
    if (!this.datamodelParseError) {
      this.datamodelMode = 'gui';
    }
  }

  addVariable(variables: DataModelVariable[]): void {
    const variable = this.createVariable(this.uniqueVariableName(variables, 'newVariable'));
    variable.editorOpen = true;
    variables.push(variable);
    this.syncDatamodelJson();
    this.scheduleAutosaveDatamodel();
    this.scrollToBottom();
  }

  duplicateVariable(variables: DataModelVariable[], index: number): void {
    const duplicate = this.normalizeVariable(this.serializeVariable(variables[index]), variables[index].name);
    duplicate.name = this.uniqueVariableName(variables, `${variables[index].name || 'variable'}Copy`);
    variables.splice(index + 1, 0, duplicate);
    this.syncDatamodelJson();
    this.scheduleAutosaveDatamodel();
  }

  removeVariable(variables: DataModelVariable[], index: number): void {
    const variableName = variables[index].name || this.t('HubUnnamedVariable');
    if (!confirm(this.t('HubDeleteVariableConfirmation').replace('{variable}', variableName))) return;
    variables.splice(index, 1);
    this.syncDatamodelJson();
    this.scheduleAutosaveDatamodel();
  }

  moveVariable(variables: DataModelVariable[], index: number, direction: -1 | 1): void {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= variables.length) return;
    const [variable] = variables.splice(index, 1);
    variables.splice(targetIndex, 0, variable);
    this.syncDatamodelJson();
    this.scheduleAutosaveDatamodel();
  }

  toggleVariableEditor(variable: DataModelVariable): void {
    variable.editorOpen = !variable.editorOpen;
  }

  toggleVariableChildren(variable: DataModelVariable): void {
    variable.childrenOpen = !variable.childrenOpen;
  }

  changeVariableType(variable: DataModelVariable, typeName: string): void {
    variable.type = this.createType(this.typeNameValue(typeName, 'type.typeName'));
    this.syncDatamodelJson();
    this.scheduleAutosaveDatamodel();
  }

  addTextOption(textType: DataModelTextType): void {
    textType.hasOptions = true;
    textType.optionsList.push({value: '', enabledCondition: 'true'});
    this.syncDatamodelJson();
    this.scheduleAutosaveDatamodel();
  }

  removeTextOption(textType: DataModelTextType, index: number): void {
    textType.optionsList.splice(index, 1);
    this.syncDatamodelJson();
    this.scheduleAutosaveDatamodel();
  }

  addScreenElement(elements: ScreenElement[]): void {
    const element = this.createScreenElement('control');
    element.editorOpen = true;
    elements.push(element);
    this.syncSelectedScreenJson();
    this.scheduleAutosaveSelectedScreen();
    this.scrollToBottom();
  }

  duplicateScreenElement(elements: ScreenElement[], index: number): void {
    const duplicate = this.normalizeScreenElement(this.serializeScreenElement(elements[index]), `#${index + 1}`);
    duplicate.editorOpen = true;
    elements.splice(index + 1, 0, duplicate);
    this.syncSelectedScreenJson();
    this.scheduleAutosaveSelectedScreen();
  }

  removeScreenElement(elements: ScreenElement[], index: number): void {
    if (!confirm(this.t('HubDeleteScreenElementConfirmation'))) return;
    elements.splice(index, 1);
    this.syncSelectedScreenJson();
    this.scheduleAutosaveSelectedScreen();
  }

  moveScreenElement(elements: ScreenElement[], index: number, direction: -1 | 1): void {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= elements.length) return;
    const [element] = elements.splice(index, 1);
    elements.splice(targetIndex, 0, element);
    this.syncSelectedScreenJson();
    this.scheduleAutosaveSelectedScreen();
  }

  toggleScreenElementEditor(element: ScreenElement): void {
    element.editorOpen = !element.editorOpen;
  }

  toggleScreenElementChildren(element: ScreenElement): void {
    element.childrenOpen = !element.childrenOpen;
  }

  changeScreenElementType(element: ScreenElement, typeName: string): void {
    const next = this.createScreenElement(this.screenElementTypeValue(typeName, 'type'));
    next.editorOpen = true;
    next.children = element.children;
    next.childrenOpen = this.hasScreenChildren(next) || element.childrenOpen;
    Object.assign(element, next);
    this.syncSelectedScreenJson();
    this.scheduleAutosaveSelectedScreen();
  }

  screenState(screen: HubProcessEditorScreen): ScreenEditorState {
    if (!this.screenEditorStates[screen.id]) {
      this.screenEditorStates[screen.id] = this.createScreenEditorState(screen.json);
    }
    return this.screenEditorStates[screen.id];
  }

  toggleScreenMode(screen: HubProcessEditorScreen): void {
    const state = this.screenState(screen);
    if (state.mode === 'gui') {
      this.syncScreenJson(screen, false);
      state.mode = 'json';
      return;
    }

    this.applyScreenJson(screen);
    if (!this.screenState(screen).parseError) {
      this.screenState(screen).mode = 'gui';
    }
  }

  onScreenJsonChange(screen: HubProcessEditorScreen): void {
    this.screenState(screen).rawDirty = true;
    this.screenDirtyIds.add(screen.id);
    this.scheduleAutosaveScreen(screen, false, this.fieldAutosaveDelayMs);
  }

  applyScreenJson(screen: HubProcessEditorScreen, markDirty = true): void {
    const currentMode = this.screenState(screen).mode;
    const wasRawDirty = this.screenState(screen).rawDirty;
    const parsed = this.createScreenEditorState(screen.json, currentMode);
    this.screenEditorStates[screen.id] = parsed;
    if (!parsed.parseError) {
      parsed.rawDirty = false;
      this.syncScreenJson(screen, markDirty && wasRawDirty);
      this.scheduleAutosaveScreen(screen, markDirty && wasRawDirty);
    }
  }

  syncDatamodelJson(markDirty = true): void {
    if (!this.data || !this.datamodelVisualReady) return;
    this.data.datamodel.json = JSON.stringify(this.datamodelVariables.map((variable) => this.serializeVariable(variable)), null, 4);
    this.datamodelParseError = null;
    this.rawDatamodelDirty = false;
    if (markDirty) {
      this.datamodelDirty = true;
    }
  }

  textType(type: DataModelType): DataModelTextType | null {
    return type.typeName === 'Text' ? type : null;
  }

  numberType(type: DataModelType): DataModelNumberType | null {
    return type.typeName === 'Number' ? type : null;
  }

  lengthType(type: DataModelType): DataModelLengthType | null {
    return type.typeName === 'Email' || type.typeName === 'Phone' ? type : null;
  }

  fileType(type: DataModelType): DataModelFileType | null {
    return type.typeName === 'File' ? type : null;
  }

  componentListType(type: DataModelType): DataModelComponentListType | null {
    return type.typeName === 'ComponentList' ? type : null;
  }

  typeLabel(typeName: DataModelTypeName): string {
    return this.t(this.dataModelTypes.find((type) => type.value === typeName)?.labelKey || typeName);
  }

  typeIcon(typeName: DataModelTypeName): string {
    if (typeName === 'Text') return 'fonts';
    if (typeName === 'Number') return '123';
    if (typeName === 'Boolean') return 'toggle-on';
    if (typeName === 'Email') return 'envelope';
    if (typeName === 'Phone') return 'telephone';
    if (typeName === 'Country') return 'globe-europe-africa';
    if (typeName === 'ChUid') return 'building-check';
    if (typeName === 'File') return 'paperclip';
    return 'collection';
  }

  helpText(tooltipKey: string, helpKey: string): string {
    return `${this.t(tooltipKey)}\n\n${this.t(helpKey)}`;
  }

  controlName(path: string, index: number, name: string): string {
    return `datamodel-${path}-${index}-${name}`;
  }

  childPath(path: string, index: number): string {
    return `${path}-${index}-children`;
  }

  screenControlName(path: string, index: number, name: string): string {
    return `screen-${path}-${index}-${name}`;
  }

  trackVariable(index: number, variable: DataModelVariable): string {
    return `${index}-${variable.name}-${variable.type.typeName}`;
  }

  trackScreenElement(index: number, element: ScreenElement): string {
    return `${index}-${element.type}`;
  }

  addScreen(): void {
    const screenId = this.newScreenId.trim();
    if (!screenId) return;
    this.save('addScreen', {screenId}, this.t('HubScreenAddedMessage'));
    this.selectedScreenId = screenId;
    this.newScreenId = '';
  }

  syncSelectedScreenJson(): void {
    const screen = this.selectedScreen();
    if (!screen) return;
    this.syncScreenJson(screen);
  }

  markWorkflowDirty(change: string | {json: string; debounce?: boolean}): void {
    if (!this.data) return;
    const json = typeof change === 'string' ? change : change.json;
    this.data.workflow.json = json;
    this.workflowDirty = true;
    this.scheduleAutosaveWorkflow(json, false, typeof change !== 'string' && change.debounce ? this.fieldAutosaveDelayMs : this.autosaveDelayMs);
  }

  syncScreenField(): void {
    this.syncSelectedScreenJson();
    this.scheduleAutosaveSelectedScreen(this.fieldAutosaveDelayMs);
  }

  syncDatamodelField(): void {
    this.syncDatamodelJson();
    this.scheduleAutosaveDatamodel(true, this.fieldAutosaveDelayMs);
  }

  hasUnsavedChanges(): boolean {
    return this.hasAutosaveWork() || this.hasDirtyChanges();
  }

  unsavedChangesMessage(): string {
    return this.t('HubUnsavedChangesWarning');
  }

  selectedScreen(): HubProcessEditorScreen | null {
    return this.data?.screens.find((screen) => screen.id === this.selectedScreenId) || null;
  }

  screenElementLabel(element: ScreenElement): string {
    return this.t(this.screenElementTypes.find((type) => type.value === element.type)?.labelKey || element.type);
  }

  screenElementIcon(element: ScreenElement): string {
    return this.screenElementTypes.find((type) => type.value === element.type)?.icon || 'square';
  }

  screenElementTitle(element: ScreenElement): string {
    return element.binding || element.text || element.label || element.script || this.screenElementLabel(element);
  }

  hasScreenChildren(element: ScreenElement): boolean {
    return element.type === 'twocolumns' || element.type === 'componentlist';
  }

  screenModifiedDate(timestamp: number): Date | null {
    if (!timestamp) return null;
    return new Date(timestamp * 1000);
  }

  selectedTypeLabel(): string {
    return this.data?.typeOptions.find((option) => option.value === this.data?.process.type)?.label || this.data?.process.type || '';
  }

  selectedLocusLabel(): string {
    return this.data?.locusOptions.find((option) => option.value === this.data?.process.locus)?.label || this.data?.process.locus || '';
  }

  t(key: string): string {
    return this.hubApi.translate(key) || key;
  }

  private reload(): void {
    this.hubApi.loadProcessEditor(this.processDefinitionId).subscribe({
      next: (data) => {
        this.setData(data);
        if (!this.selectedScreenId && data.screens.length > 0) {
          this.selectedScreenId = data.screens[0].id;
        }
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || this.t('HubProcessEditorLoadFailedMessage');
        this.loading = false;
      }
    });
  }

  private save(action: string, payload: Record<string, unknown>, message: string): void {
    this.error = '';
    this.success = '';
    const draft = this.captureUnsavedDraft(action, payload);
    this.hubApi.saveProcessEditor(this.processDefinitionId, action, payload).subscribe({
      next: (data) => {
        this.setData(data);
        this.restoreUnsavedDraft(draft);
        if (this.activeTab === 'screens' && !data.screens.some((screen) => screen.id === this.selectedScreenId)) {
          this.selectedScreenId = data.screens[0]?.id || '';
        }
        this.success = message;
      },
      error: (error) => this.error = error.error?.message || this.t('HubProcessSaveFailedMessage')
    });
  }

  private restoreDatamodelArchive(direction: 'undo' | 'redo'): void {
    if (!this.data?.datamodel.archive[direction === 'undo' ? 'canUndo' : 'canRedo']) return;
    this.cancelAutosave(this.historyKey('datamodel'));
    this.save('restoreDatamodelArchive', {direction}, '');
  }

  private restoreScreenArchive(screen: HubProcessEditorScreen, direction: 'undo' | 'redo'): void {
    if (!screen.archive[direction === 'undo' ? 'canUndo' : 'canRedo']) return;
    this.cancelAutosave(this.historyKey('screen', screen.id));
    this.save('restoreScreenArchive', {screenId: screen.id, direction}, '');
  }

  private restoreWorkflowArchive(direction: 'undo' | 'redo'): void {
    if (!this.data?.workflow.archive[direction === 'undo' ? 'canUndo' : 'canRedo']) return;
    this.cancelAutosave(this.historyKey('workflow'));
    this.save('restoreWorkflowArchive', {direction}, '');
  }

  private setData(data: HubProcessEditorResponse): void {
    this.data = data;
    this.rawDatamodelDirty = false;
    this.parseDatamodelJson(data.datamodel.json);
    this.parseScreens();
    this.clearDirtyState();
  }

  private historyKey(type: 'datamodel' | 'workflow' | 'screen', id = ''): string {
    return id ? `${type}:${id}` : type;
  }

  private clearDirtyState(): void {
    this.propertiesDirty = false;
    this.datamodelDirty = false;
    this.workflowDirty = false;
    this.screenDirtyIds.clear();
  }

  private propertiesSnapshot(): string {
    if (!this.data) return '';
    return JSON.stringify({
      type: this.data.process.type,
      locus: this.data.process.locus,
      description: this.data.process.description
    });
  }

  private autosave(key: string, request: AutosaveRequest): void {
    this.error = '';
    this.success = '';
    this.autosaveInFlight.add(key);
    this.hubApi.saveProcessEditor(this.processDefinitionId, request.action, request.payload).subscribe({
      next: (data) => {
        this.applySavedTimestamps(data);
        this.clearDirtyForAutosave(request);
        this.autosaveInFlight.delete(key);
        if (this.queuedAutosaves.has(key)) {
          this.queuedAutosaves.delete(key);
          this.runAutosave(key);
        }
      },
      error: (error) => {
        this.autosaveInFlight.delete(key);
        this.error = error.error?.message || this.t('HubProcessSaveFailedMessage');
      }
    });
  }

  private scheduleAutosave(key: string, request: AutosaveRequest, delay = this.autosaveDelayMs): void {
    this.autosaveRequests[key] = request;
    if (this.autosaveTimers[key]) clearTimeout(this.autosaveTimers[key]);
    this.autosaveTimers[key] = setTimeout(() => this.runAutosave(key), delay);
  }

  private cancelAutosave(key: string): void {
    if (this.autosaveTimers[key]) clearTimeout(this.autosaveTimers[key]);
    delete this.autosaveTimers[key];
    delete this.autosaveRequests[key];
    this.queuedAutosaves.delete(key);
  }

  private runAutosave(key: string): void {
    delete this.autosaveTimers[key];
    const request = this.autosaveRequests[key];
    if (!request) return;
    if (this.autosaveInFlight.has(key)) {
      this.queuedAutosaves.add(key);
      return;
    }
    this.autosave(key, request);
  }

  private scheduleAutosaveDatamodel(_recordCurrent = true, delay = this.autosaveDelayMs): void {
    const key = this.historyKey('datamodel');
    if (!this.data || !this.validateDatamodelJson(this.data.datamodel.json)) {
      this.cancelAutosave(key);
      return;
    }
    this.scheduleAutosave(key, {action: 'saveDatamodel', payload: {json: this.data.datamodel.json}}, delay);
  }

  private validateDatamodelJson(json: string): boolean {
    try {
      const decoded = JSON.parse(json || '[]') as unknown;
      if (!Array.isArray(decoded)) throw new Error(this.t('HubDataModelArrayError'));
      decoded.forEach((variable, index) => this.normalizeVariable(variable, `#${index + 1}`));
      this.datamodelParseError = null;
      return true;
    } catch (error) {
      this.datamodelParseError = this.jsonEditorError(error, json);
      return false;
    }
  }

  private scheduleAutosaveProperties(delay = this.autosaveDelayMs): void {
    if (!this.data) return;
    this.scheduleAutosave('properties', {
      action: 'updateProperties',
      payload: {
        type: this.data.process.type,
        locus: this.data.process.locus,
        description: this.data.process.description
      }
    }, delay);
  }

  private scheduleAutosaveScreen(screen: HubProcessEditorScreen, _recordCurrent = true, delay = this.autosaveDelayMs): void {
    const key = this.historyKey('screen', screen.id);
    if (!this.validateRawScreenJson(screen)) {
      this.cancelAutosave(key);
      return;
    }
    this.scheduleAutosave(key, {action: 'saveScreen', payload: {screenId: screen.id, json: screen.json}}, delay);
  }

  private scheduleAutosaveSelectedScreen(delay = this.autosaveDelayMs): void {
    const screen = this.selectedScreen();
    if (!screen) return;
    this.scheduleAutosaveScreen(screen, true, delay);
  }

  private scheduleAutosaveWorkflow(json: string, _recordCurrent = true, delay = this.autosaveDelayMs): void {
    const key = this.historyKey('workflow');
    if (!this.isValidWorkflowJson(json)) {
      this.cancelAutosave(key);
      return;
    }
    this.scheduleAutosave(key, {action: 'saveWorkflow', payload: {json}}, delay);
  }

  private isValidWorkflowJson(json: string): boolean {
    try {
      const decoded = JSON.parse(json || '{}') as unknown;
      if (decoded === null || typeof decoded !== 'object' || Array.isArray(decoded)) return false;
      const workflow = decoded as Record<string, unknown>;
      return ['tasks', 'events', 'gateways', 'connections'].every((key) => Array.isArray(workflow[key]));
    } catch {
      return false;
    }
  }

  private applySavedTimestamps(data: HubProcessEditorResponse): void {
    if (!this.data) return;
    this.data.datamodel.error = data.datamodel.error;
    this.data.datamodel.lastModified = data.datamodel.lastModified;
    this.data.datamodel.archive = data.datamodel.archive;
    this.data.workflow.error = data.workflow.error;
    this.data.workflow.lastModified = data.workflow.lastModified;
    this.data.workflow.archive = data.workflow.archive;
    for (const updatedScreen of data.screens) {
      const screen = this.data.screens.find((entry) => entry.id === updatedScreen.id);
      if (screen) {
        screen.error = updatedScreen.error;
        screen.componentCount = updatedScreen.componentCount;
        screen.controlCount = updatedScreen.controlCount;
        screen.lastModified = updatedScreen.lastModified;
        screen.archive = updatedScreen.archive;
      }
    }
  }

  private clearDirtyForAutosave(request: AutosaveRequest): void {
    if (!this.data) return;
    if (request.action === 'updateProperties' && this.propertiesSnapshot() === JSON.stringify(request.payload)) this.propertiesDirty = false;
    if (request.action === 'saveDatamodel' && this.data.datamodel.json === request.payload['json']) this.datamodelDirty = false;
    if (request.action === 'saveWorkflow' && this.data.workflow.json === request.payload['json']) this.workflowDirty = false;
    if (request.action === 'saveScreen') {
      const screenId = String(request.payload['screenId'] || '');
      const screen = this.data.screens.find((entry) => entry.id === screenId);
      if (screen?.json === request.payload['json']) this.screenDirtyIds.delete(screenId);
    }
  }

  private captureUnsavedDraft(action: string, payload: Record<string, unknown>): ProcessEditorDraft {
    const draft: ProcessEditorDraft = {screens: {}};
    if (!this.data) return draft;

    if (this.propertiesDirty && action !== 'updateProperties') {
      draft.properties = {
        type: this.data.process.type,
        locus: this.data.process.locus,
        description: this.data.process.description
      };
    }

    if (this.datamodelDirty && action !== 'saveDatamodel' && action !== 'restoreDatamodelArchive') {
      draft.datamodel = {
        json: this.data.datamodel.json,
        variables: this.clone(this.datamodelVariables),
        parseError: this.datamodelParseError,
        visualReady: this.datamodelVisualReady,
        mode: this.datamodelMode,
        rawDirty: this.rawDatamodelDirty
      };
    }

    const savedScreenId = action === 'saveScreen' || action === 'restoreScreenArchive' ? String(payload['screenId'] || '') : '';
    for (const screen of this.data.screens) {
      if (!this.screenDirtyIds.has(screen.id) || screen.id === savedScreenId) continue;
      draft.screens[screen.id] = {
        json: screen.json,
        state: this.clone(this.screenState(screen))
      };
    }
    if (this.workflowDirty && action !== 'saveWorkflow' && action !== 'restoreWorkflowArchive') {
      draft.workflow = {json: this.data.workflow.json};
    }
    return draft;
  }

  private restoreUnsavedDraft(draft: ProcessEditorDraft): void {
    if (!this.data) return;

    if (draft.properties) {
      this.data.process.type = draft.properties.type;
      this.data.process.locus = draft.properties.locus;
      this.data.process.description = draft.properties.description;
      this.propertiesDirty = true;
    }

    if (draft.datamodel) {
      this.data.datamodel.json = draft.datamodel.json;
      this.datamodelVariables = this.clone(draft.datamodel.variables);
      this.datamodelParseError = draft.datamodel.parseError;
      this.datamodelVisualReady = draft.datamodel.visualReady;
      this.datamodelMode = draft.datamodel.mode;
      this.rawDatamodelDirty = draft.datamodel.rawDirty;
      this.datamodelDirty = true;
    }

    for (const [screenId, screenDraft] of Object.entries(draft.screens)) {
      const screen = this.data.screens.find((entry) => entry.id === screenId);
      if (!screen) continue;
      screen.json = screenDraft.json;
      this.screenEditorStates[screenId] = this.clone(screenDraft.state);
      this.screenDirtyIds.add(screenId);
    }

    if (draft.workflow) {
      this.data.workflow = {...this.data.workflow, json: draft.workflow.json};
      this.workflowDirty = true;
    }
  }

  private hasAutosaveWork(): boolean {
    return this.autosaveInFlight.size > 0 || this.queuedAutosaves.size > 0 || Object.keys(this.autosaveTimers).length > 0;
  }

  private hasDirtyChanges(): boolean {
    return this.propertiesDirty || this.datamodelDirty || this.workflowDirty || this.screenDirtyIds.size > 0;
  }

  private scrollToBottom(): void {
    if (!this.viewInitialized) return;
    setTimeout(() => window.scrollTo({top: document.documentElement.scrollHeight, behavior: 'smooth'}));
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }

  private parseScreens(): void {
    if (!this.data) return;
    this.screenEditorStates = Object.fromEntries(this.data.screens.map((screen) => [screen.id, this.createScreenEditorState(screen.json)]));
  }

  private createScreenEditorState(json: string, mode: 'gui' | 'json' = 'gui'): ScreenEditorState {
    try {
      const decoded = JSON.parse(json || '[]') as unknown;
      if (!Array.isArray(decoded)) {
        throw new Error(this.t('HubScreenJsonArrayError'));
      }
      return {
        visualReady: true,
        parseError: null,
        mode,
        rawDirty: false,
        elements: decoded.map((element, index) => this.normalizeScreenElement(element, `#${index + 1}`))
      };
    } catch (error) {
      return {
        visualReady: false,
        parseError: this.jsonEditorError(error, json),
        mode: 'json',
        rawDirty: false,
        elements: []
      };
    }
  }

  private normalizeScreenElement(value: unknown, path: string): ScreenElement {
    const raw = this.recordValue(value, path);
    const type = this.screenElementTypeValue(raw['type'], `${path}.type`);
    const valueType = raw['valueType'] !== undefined ? this.recordValue(raw['valueType'], `${path}.valueType`) : {};
    return {
      type,
      binding: this.stringValue(raw['binding'], ''),
      text: this.stringValue(raw['text'], ''),
      label: this.stringValue(raw['label'], ''),
      visibilityCondition: this.stringValue(raw['visibilityCondition'], ''),
      textSource: this.screenTextSourceValue(raw['textSource'], 'text'),
      script: this.stringValue(raw['script'], ''),
      valueTypeEnabled: raw['valueType'] !== undefined,
      valueTypeType: this.stringValue(valueType['type'], 'number'),
      valueTypeStep: this.numberValue(valueType['step']),
      editorOpen: false,
      childrenOpen: false,
      children: this.arrayValue(raw['children']).map((child, index) => this.normalizeScreenElement(child, `${path}.children[${index}]`)),
      raw: {...raw}
    };
  }

  private serializeScreenElement(element: ScreenElement): Record<string, unknown> {
    const raw = {...element.raw};
    raw['type'] = this.screenElementJsonType(element);
    this.setOptionalString(raw, 'visibilityCondition', element.visibilityCondition);

    if (element.type === 'control') {
      raw['binding'] = element.binding;
      this.setOptionalString(raw, 'label', element.label);
      delete raw['text'];
      delete raw['textSource'];
      delete raw['script'];
      delete raw['valueType'];
      delete raw['children'];
    } else if (element.type === 'title') {
      raw['text'] = element.text;
      delete raw['binding'];
      delete raw['label'];
      delete raw['textSource'];
      delete raw['script'];
      delete raw['valueType'];
      delete raw['children'];
    } else if (element.type === 'label' || element.type === 'infobox') {
      if (element.textSource === 'text') {
        raw['text'] = element.text;
        if (element.raw['textSource'] !== undefined) {
          raw['textSource'] = 'text';
        } else {
          delete raw['textSource'];
        }
        delete raw['binding'];
      } else {
        raw['textSource'] = element.textSource;
        raw['binding'] = element.binding;
        delete raw['text'];
      }
      delete raw['label'];
      delete raw['script'];
      delete raw['valueType'];
      delete raw['children'];
    } else if (element.type === 'output') {
      raw['label'] = element.label;
      raw['script'] = element.script;
      if (element.valueTypeEnabled) {
        raw['valueType'] = {
          type: element.valueTypeType || 'number',
          step: this.numberValue(element.valueTypeStep)
        };
      } else {
        delete raw['valueType'];
      }
      delete raw['binding'];
      delete raw['text'];
      delete raw['textSource'];
      delete raw['children'];
    } else if (element.type === 'twocolumns') {
      raw['children'] = element.children.map((child) => this.serializeScreenElement(child));
      delete raw['binding'];
      delete raw['text'];
      delete raw['label'];
      delete raw['textSource'];
      delete raw['script'];
      delete raw['valueType'];
    } else if (element.type === 'componentlist') {
      raw['binding'] = element.binding;
      raw['children'] = element.children.map((child) => this.serializeScreenElement(child));
      delete raw['text'];
      delete raw['label'];
      delete raw['textSource'];
      delete raw['script'];
      delete raw['valueType'];
    }

    return raw;
  }

  private screenElementJsonType(element: ScreenElement): string {
    return element.type === 'componentlist' ? 'ComponentList' : element.type;
  }

  private syncScreenJson(screen: HubProcessEditorScreen, markDirty = true): void {
    const state = this.screenState(screen);
    if (!state.visualReady) return;
    screen.json = JSON.stringify(state.elements.map((element) => this.serializeScreenElement(element)), null, 4);
    state.rawDirty = false;
    state.parseError = null;
    if (markDirty) {
      this.screenDirtyIds.add(screen.id);
    }
  }

  private validateRawScreenJson(screen: HubProcessEditorScreen): boolean {
    const state = this.screenState(screen);
    try {
      const decoded = JSON.parse(screen.json || '[]') as unknown;
      if (!Array.isArray(decoded)) {
        throw new Error(this.t('HubScreenJsonArrayError'));
      }
      state.parseError = null;
      return true;
    } catch (error) {
      state.parseError = this.jsonEditorError(error, screen.json);
      return false;
    }
  }

  private createScreenElement(type: ScreenElementType): ScreenElement {
    return {
      type,
      binding: '',
      text: '',
      label: '',
      visibilityCondition: '',
      textSource: 'text',
      script: '',
      valueTypeEnabled: false,
      valueTypeType: 'number',
      valueTypeStep: null,
      editorOpen: false,
      childrenOpen: false,
      children: [],
      raw: {type}
    };
  }

  private setOptionalString(raw: Record<string, unknown>, key: string, value: string): void {
    if (value === '') {
      delete raw[key];
      return;
    }
    raw[key] = value;
  }

  private parseDatamodelJson(json: string): void {
    try {
      const decoded = JSON.parse(json || '[]') as unknown;
      if (!Array.isArray(decoded)) {
        throw new Error(this.t('HubDataModelArrayError'));
      }
      this.datamodelVariables = decoded.map((variable, index) => this.normalizeVariable(variable, `#${index + 1}`));
      this.datamodelParseError = null;
      this.datamodelVisualReady = true;
    } catch (error) {
      this.datamodelParseError = this.jsonEditorError(error, json);
      this.datamodelVisualReady = false;
      this.datamodelVariables = [];
    }
  }

  private normalizeVariable(value: unknown, path: string): DataModelVariable {
    const variable = this.recordValue(value, path);
    const type = this.recordValue(variable['type'], `${path}.type`);
    const typeName = this.typeNameValue(type['typeName'], `${path}.type.typeName`);

    return {
      name: this.stringValue(variable['name'], ''),
      scopeMode: this.scopeModeValue(variable['scopeMode'] ?? variable['modus'], 'readwrite'),
      mandatory: this.mandatoryValue(variable['mandatory'], 'optional'),
      visibilityCondition: this.stringValue(variable['visibilityCondition'], 'true'),
      enabledCondition: this.stringValue(variable['enabledCondition'], 'true'),
      editorOpen: false,
      childrenOpen: false,
      type: this.normalizeType(typeName, type, path)
    };
  }

  private normalizeType(typeName: DataModelTypeName, type: Record<string, unknown>, path: string): DataModelType {
    if (typeName === 'Text') {
      return {
        typeName,
        maxLength: this.numberValue(type['maxLength']),
        hasOptions: this.booleanValue(type['hasOptions'], false),
        multiline: this.booleanValue(type['multiline'], false),
        optionsList: this.arrayValue(type['optionsList']).map((option, index) => this.normalizeTextOption(option, `${path}.type.optionsList[${index}]`))
      };
    }
    if (typeName === 'Number') {
      return {
        typeName,
        defaultValue: this.numberValue(type['defaultValue']),
        minValue: this.numberValue(type['minValue']),
        maxValue: this.numberValue(type['maxValue']),
        step: this.numberValue(type['step'])
      };
    }
    if (typeName === 'Email' || typeName === 'Phone') {
      return {typeName, maxLength: this.numberValue(type['maxLength'])};
    }
    if (typeName === 'File') {
      return {
        typeName,
        maxSize: this.numberValue(type['maxSize']),
        allowMultiple: this.booleanValue(type['allowMultiple'], false)
      };
    }
    if (typeName === 'ComponentList') {
      const keyVariableName = this.stringValue(type['key_variable_name'] ?? type['id'], 'id');
      return {
        typeName,
        key_variable_name: keyVariableName,
        id: keyVariableName,
        children: this.arrayValue(type['children']).map((child, index) => this.normalizeVariable(child, `${path}.type.children[${index}]`))
      };
    }
    return {typeName};
  }

  private normalizeTextOption(value: unknown, path: string): DataModelTextOption {
    const option = this.recordValue(value, path);
    return {
      value: this.stringValue(option['value'], ''),
      enabledCondition: this.stringValue(option['enabledCondition'], 'true')
    };
  }

  private serializeVariable(variable: DataModelVariable): Record<string, unknown> {
    return {
      name: variable.name,
      scopeMode: this.scopeModeValue(variable.scopeMode, 'readwrite'),
      mandatory: this.mandatoryValue(variable.mandatory, 'optional'),
      visibilityCondition: variable.visibilityCondition || '',
      enabledCondition: variable.enabledCondition || '',
      type: this.serializeType(variable.type)
    };
  }

  private serializeType(type: DataModelType): Record<string, unknown> {
    if (type.typeName === 'Text') {
      return {
        typeName: type.typeName,
        maxLength: this.numberValue(type.maxLength),
        hasOptions: !!type.hasOptions,
        multiline: !!type.multiline,
        optionsList: type.optionsList.map((option) => ({
          value: option.value,
          enabledCondition: option.enabledCondition || 'true'
        }))
      };
    }
    if (type.typeName === 'Number') {
      return {
        typeName: type.typeName,
        defaultValue: this.numberValue(type.defaultValue),
        minValue: this.numberValue(type.minValue),
        maxValue: this.numberValue(type.maxValue),
        step: this.numberValue(type.step)
      };
    }
    if (type.typeName === 'Email' || type.typeName === 'Phone') {
      return {typeName: type.typeName, maxLength: this.numberValue(type.maxLength)};
    }
    if (type.typeName === 'File') {
      return {
        typeName: type.typeName,
        maxSize: this.numberValue(type.maxSize),
        allowMultiple: !!type.allowMultiple
      };
    }
    if (type.typeName === 'ComponentList') {
      const keyVariableName = type.key_variable_name || type.id || 'id';
      return {
        typeName: type.typeName,
        key_variable_name: keyVariableName,
        children: type.children.map((child) => this.serializeVariable(child)),
        id: keyVariableName
      };
    }
    return {typeName: type.typeName};
  }

  private createVariable(name: string): DataModelVariable {
    return {
      name,
      scopeMode: 'readwrite',
      mandatory: 'optional',
      visibilityCondition: 'true',
      enabledCondition: 'true',
      editorOpen: false,
      childrenOpen: false,
      type: this.createType('Text')
    };
  }

  private createType(typeName: DataModelTypeName): DataModelType {
    if (typeName === 'Text') return {typeName, maxLength: null, hasOptions: false, multiline: false, optionsList: []};
    if (typeName === 'Number') return {typeName, defaultValue: null, minValue: null, maxValue: null, step: null};
    if (typeName === 'Email' || typeName === 'Phone') return {typeName, maxLength: null};
    if (typeName === 'File') return {typeName, maxSize: null, allowMultiple: false};
    if (typeName === 'ComponentList') return {typeName, key_variable_name: 'id', id: 'id', children: []};
    return {typeName};
  }

  private uniqueVariableName(variables: DataModelVariable[], baseName: string): string {
    const normalizedBase = baseName.trim() || 'variable';
    const existingNames = new Set(variables.map((variable) => variable.name));
    if (!existingNames.has(normalizedBase)) return normalizedBase;

    let index = 2;
    while (existingNames.has(`${normalizedBase}${index}`)) {
      index++;
    }
    return `${normalizedBase}${index}`;
  }

  private recordValue(value: unknown, path: string): Record<string, unknown> {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    throw new Error(this.t('HubDataModelObjectError').replace('{path}', path));
  }

  private arrayValue(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private stringValue(value: unknown, fallback: string): string {
    return typeof value === 'string' ? value : fallback;
  }

  private booleanValue(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private numberValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private scopeModeValue(value: unknown, fallback: DataModelScopeMode): DataModelScopeMode {
    return value === 'readonly' || value === 'writeonly' || value === 'readwrite' || value === 'local' ? value : fallback;
  }

  private mandatoryValue(value: unknown, fallback: DataModelMandatory): DataModelMandatory {
    return value === 'mandatory' || value === 'optional' ? value : fallback;
  }

  private typeNameValue(value: unknown, path: string): DataModelTypeName {
    if (
      value === 'Text' ||
      value === 'Number' ||
      value === 'Boolean' ||
      value === 'Email' ||
      value === 'Phone' ||
      value === 'Country' ||
      value === 'ChUid' ||
      value === 'File' ||
      value === 'ComponentList'
    ) {
      return value;
    }
    throw new Error(this.t('HubDataModelUnsupportedTypeError').replace('{path}', path).replace('{type}', String(value || '')));
  }

  private screenElementTypeValue(value: unknown, path: string): ScreenElementType {
    if (value === 'ComponentList') return 'componentlist';
    if (
      value === 'control' ||
      value === 'title' ||
      value === 'label' ||
      value === 'infobox' ||
      value === 'output' ||
      value === 'twocolumns' ||
      value === 'componentlist'
    ) {
      return value;
    }
    throw new Error(this.t('HubScreenUnsupportedTypeError').replace('{path}', path).replace('{type}', String(value || '')));
  }

  private screenTextSourceValue(value: unknown, fallback: ScreenTextSource): ScreenTextSource {
    return value === 'text' || value === 'variable' || value === 'binding' ? value : fallback;
  }

  jsonErrorDetails(error: JsonEditorError): string[] {
    return error.details;
  }

  private jsonEditorError(error: unknown, json: string): JsonEditorError {
    const message = error instanceof Error ? error.message : String(error);
    const details: string[] = [];
    const position = this.jsonErrorPosition(message);
    if (position !== null) {
      const location = this.jsonLineColumn(json, position);
      details.push(this.t('HubJsonErrorLocation').replace('{line}', String(location.line)).replace('{column}', String(location.column)));
    }
    details.push(message);
    return {title: this.t('HubJsonVisualParseErrorTitle'), details};
  }

  private jsonErrorPosition(message: string): number | null {
    const match = message.match(/position (\d+)/i);
    return match ? Number(match[1]) : null;
  }

  private jsonLineColumn(json: string, position: number): {line: number; column: number} {
    const beforeError = json.slice(0, Math.max(0, position));
    const lines = beforeError.split('\n');
    return {line: lines.length, column: lines[lines.length - 1].length + 1};
  }
}
